import React, { useCallback, useEffect, useState } from 'react'

import { CreateItineraryFormValues } from '@/pages/AdminDashboard/components/ExternalBookings/components/ExternalBookingsModal/ExternalBookingsModal.types'
import { updateTripDetailsForAdmin } from '@/redux/states/admin/trips/updateTripDetailsForAdmin/updateTripDetailsForAdmin.slice'
import { UpdateTripDetailsForAdminRequestPayload } from '@/redux/states/admin/trips/updateTripDetailsForAdmin/updateTripDetailsForAdmin.types'
import { getCountriesAndCities } from '@/redux/states/flights/getCountriesAndCities/getCountriesAndCities.slice'
import { getExternalAirlines } from '@/redux/states/flights/getExternalAirlines/getExternalAirlines.slice'
import { GetExternalAirlinesRequestPayload } from '@/redux/states/flights/getExternalAirlines/getExternalAirlines.types'
import { RootState } from '@/redux/store/store.types'
import Snackbar from '@/shared/components/Snackbar/Snackbar.functions'
import { joinStringsWithBullet } from '@/shared/functions/String/string.functions'
import { useAppDispatch } from '@/shared/hooks/redux.hooks'
import { useSelector } from 'react-redux'

import ExternalBookingsModal from '@/pages/AdminDashboard/components/ExternalBookings/components/ExternalBookingsModal/ExternalBookingsModal.component'

import { EditTripDetailsModalProps } from './EditTripDetailsModal.types'

const EditTripDetailsModal: React.FC<EditTripDetailsModalProps> = (props) => {
  const { open, onClose, tripData } = props

  const [allAirports, setAllAirports] = useState<{ value: string; label: string }[]>([])
  const [allAirlines, setAllAirlines] = useState<{ value: string; label: string }[]>([])

  const dispatch = useAppDispatch()

  const {
    loading: updateTripLoading,
    success: updateTripSuccess,
    error: updateTripError
  } = useSelector((state: RootState) => state.updateTripDetailsForAdmin)

  const { success: airlinesSuccess } = useSelector((state: RootState) => state.getExternalAirlines)

  const { success: countriesAndCitiesSuccess } = useSelector((state: RootState) => state.getCountriesAndCities)

  const { success: tripDetailsSuccess } = useSelector((state: RootState) => state.getTripDetailsForAdmin)

  useEffect(() => {
    if (open) {
      // Fetch airports and airlines data when modal opens
      const getExternalAirlinesPayload: GetExternalAirlinesRequestPayload = {
        onSuccess: () => {
          // Airlines fetched successfully
        }
      }

      dispatch(getExternalAirlines(getExternalAirlinesPayload))
      dispatch(getCountriesAndCities({}))
    }
  }, [dispatch, open])

  useEffect(() => {
    if (countriesAndCitiesSuccess) {
      const allAirports = countriesAndCitiesSuccess.data
        .flatMap((country) =>
          country.cities.flatMap((city) =>
            city.airports.map((airport) => ({
              value: airport.iataCode,
              label: joinStringsWithBullet([
                `${airport.iataCode} (${airport.airportName})`,
                `${city.cityName}, ${country.countryName}`
              ])
            }))
          )
        )
        .sort((a, b) => a.value.localeCompare(b.value))

      setAllAirports(allAirports)
    }
  }, [countriesAndCitiesSuccess])

  useEffect(() => {
    if (airlinesSuccess) {
      setAllAirlines(
        airlinesSuccess.data
          .map((airline) => ({
            value: airline.id,
            label: airline.name
          }))
          .sort((a, b) => a.label.localeCompare(b.label))
      )
    }
  }, [airlinesSuccess])

  useEffect(() => {
    if (updateTripSuccess) {
      Snackbar.show({
        message: 'Trip details updated successfully!',
        severity: 'success'
      })

      onClose()

      // Refresh trip details data
      if (tripDetailsSuccess) {
        // The parent component should handle refreshing the data
        // This could be enhanced by passing a refresh callback from parent
      }
    }
  }, [updateTripSuccess, onClose, tripDetailsSuccess])

  useEffect(() => {
    if (updateTripError) {
      Snackbar.show({
        message: updateTripError.userFriendlyMessage || 'Failed to update trip details. Please try again.',
        severity: 'error'
      })
    }
  }, [updateTripError])

  const handleUpdateTrip = useCallback(
    (values: CreateItineraryFormValues) => {
      if (!tripData) return

      const flightItineraryId = tripData.tripDetails.flightItineraryDetails.id.toString()

      const payload: UpdateTripDetailsForAdminRequestPayload = {
        request: {
          flightItineraryId,
          tripType: values.tripType,
          tripPrice: Number(values.tripPrice),
          tripCurrency: values.tripCurrency,
          cabinClass: values.cabinClass,
          slices: values.slices.map((slice) => ({
            originAirportIataCode: slice.originAirportIataCode,
            destinationAirportIataCode: slice.destinationAirportIataCode,
            travelDate: slice.travelDate,
            segments: slice.segments.map((segment) => ({
              flightNumber: segment.flightNumber,
              airlineId: segment.airlineId,
              departureTime: segment.departureTime,
              arrivalTime: segment.arrivalTime,
              originAirportIataCode: segment.originAirportIataCode,
              destinationAirportIataCode: segment.destinationAirportIataCode
            }))
          }))
        },
        onSuccess: () => {
          // Success handled in useEffect above
        }
      }

      dispatch(updateTripDetailsForAdmin(payload))
    },
    [dispatch, tripData]
  )

  const handleCloseModal = useCallback(() => {
    onClose()
  }, [onClose])

  return (
    <ExternalBookingsModal
      open={open}
      onClose={handleCloseModal}
      mode="edit"
      editTripData={tripData}
      onUpdate={handleUpdateTrip}
      updateTripLoading={updateTripLoading}
      bookingDetails={null}
      onSubmit={() => {
        // Do nothing
      }}
      onDeny={() => {
        // Do nothing
      }}
      denyBookingRequestLoading={false}
      airports={allAirports}
      airlines={allAirlines}
      createItineraryLoading={false}
    />
  )
}

export default EditTripDetailsModal
